import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { mockData } from '../data/mock';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const lastScrollY = useRef(0);
  const headerRef = useRef(null);
  const bannerRef = useRef(null);
  const groupRef = useRef(null);
  const dropdownTimer = useRef(null);
  const dropdownBtnRef = useRef(null);
  const burgerRef = useRef(null);
  const wasMenuOpen = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';

  // Mesure les hauteurs et injecte les CSS vars
  // (uniquement pour le dropdown mobile, overlay, et contenu des pages)
  useEffect(() => {
    const updateHeight = () => {
      const navH = headerRef.current ? headerRef.current.getBoundingClientRect().height : 0;
      const bannerH = bannerRef.current ? bannerRef.current.getBoundingClientRect().height : 0;
      document.documentElement.style.setProperty('--header-nav-h', `${navH}px`);
      document.documentElement.style.setProperty('--banner-h', `${bannerH}px`);
      document.documentElement.style.setProperty('--header-h', `${navH + bannerH}px`);
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  // Reset scroll state on route change
  useEffect(() => {
    setIsHidden(false);
    setIsScrolled(false);
    lastScrollY.current = 0;
  }, [location.pathname]);

  // Scroll vers la section ciblée après une navigation react-router (/#section)
  useEffect(() => {
    if (!location.hash) return;
    const element = document.getElementById(location.hash.slice(1));
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsScrolled(currentScrollY > 50);
      if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
        setIsHidden(true);
        setIsMenuOpen(false);
      } else {
        setIsHidden(false);
      }
      lastScrollY.current = currentScrollY;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  // Échap ferme le menu mobile et le sous-menu "Notre expertise"
  useEffect(() => {
    if (!isMenuOpen && !isDropdownOpen) return;
    const handleKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (isDropdownOpen) {
        setIsDropdownOpen(false);
        dropdownBtnRef.current?.focus();
      }
      setIsMenuOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMenuOpen, isDropdownOpen]);

  // Rend le focus au bouton burger quand le menu se referme
  useEffect(() => {
    if (wasMenuOpen.current && !isMenuOpen) burgerRef.current?.focus();
    wasMenuOpen.current = isMenuOpen;
  }, [isMenuOpen]);

  const scrollToTop = () => {
    if (isHomePage) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const scrollToSection = (sectionId) => {
    if (isHomePage) {
      const element = document.getElementById(sectionId);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(`/#${sectionId}`);
    }
    setIsMenuOpen(false);
  };

  // Liens de section : <Link> réel (SEO), scroll fluide seulement si déjà sur /
  const handleSectionClick = (e, sectionId) => {
    if (isHomePage) {
      e.preventDefault();
      const element = document.getElementById(sectionId);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const handleDropdownEnter = () => {
    clearTimeout(dropdownTimer.current);
    setIsDropdownOpen(true);
  };

  const handleDropdownLeave = () => {
    dropdownTimer.current = setTimeout(() => setIsDropdownOpen(false), 200);
  };

  // Section-based links (scroll on homepage)
  const sectionLinks = [
    { id: 'accueil', label: 'Accueil' },
    { id: 'services', label: 'Services' },
    { id: 'valeurs', label: 'Pourquoi Bouhdel' },
    { id: 'realisations', label: 'Réalisations' },
    { id: 'offres-terrains', label: 'Offres terrains' },
    { id: 'agences', label: 'Agences' },
  ];

  // Page-based links (dropdown)
  const expertiseLinks = [
    { to: '/histoire', label: 'Notre histoire' },
    { to: '/ccmi', label: 'Le CCMI' },
    { to: '/re2020', label: 'Norme RE2020' },
    { to: '/realisations', label: 'Nos réalisations' },
  ];

  return (
    <>
      {/* Wrapper fixe — header et bandeau toujours collés via le flow normal */}
      <div
        ref={groupRef}
        className={`header-group ${isHidden ? 'header-group-hidden' : ''}`}
      >
        <header
          ref={headerRef}
          className={`header-main ${isScrolled ? 'header-scrolled' : ''}`}
        >
          <a href="#main" className="skip-link">Aller au contenu</a>
          <div className="header-container">
            {/* Logo */}
            <Link to="/" className="header-logo-btn" onClick={scrollToTop} aria-label="Bouhdel — retour à l'accueil">
              <img src="/logo.png" alt="Bouhdel" width="76" height="64" className="logo-img" />
            </Link>

            {/* Desktop Navigation */}
            <nav className="nav-desktop">
              {sectionLinks.map(link => (
                <Link
                  key={link.id}
                  to={`/#${link.id}`}
                  onClick={(e) => handleSectionClick(e, link.id)}
                  className="nav-link"
                >
                  {link.label}
                </Link>
              ))}

              {/* Dropdown "Notre expertise" */}
              <div
                className="nav-dropdown-wrapper"
                onMouseEnter={handleDropdownEnter}
                onMouseLeave={handleDropdownLeave}
              >
                <button
                  ref={dropdownBtnRef}
                  className="nav-link nav-link-dropdown"
                  onClick={() => setIsDropdownOpen(open => !open)}
                  aria-expanded={isDropdownOpen}
                  aria-controls="expertise-menu"
                >
                  Notre expertise
                  <ChevronDown size={16} className={`nav-chevron ${isDropdownOpen ? 'nav-chevron-open' : ''}`} />
                </button>
                <div
                  id="expertise-menu"
                  className={`nav-sub-dropdown ${isDropdownOpen ? 'nav-sub-dropdown-open' : ''}`}
                  inert={!isDropdownOpen}
                >
                  {expertiseLinks.map(link => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className="nav-sub-link"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>

              <button onClick={() => scrollToSection('contact')} className="btn-primary btn-header">Contact</button>
            </nav>

            {/* Burger Button */}
            <button
              ref={burgerRef}
              className="mobile-menu-btn"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
            >
              <span className={`burger-lines ${isMenuOpen ? 'burger-open' : ''}`}>
                <span className="burger-line" />
                <span className="burger-line" />
                <span className="burger-line" />
              </span>
            </button>
          </div>
        </header>

        {/* Bandeau — toujours collé sous la barre de nav grâce au flow normal */}
        <div className="top-banner" ref={bannerRef}>
          <div className="marquee-content">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="marquee-item">
                <span className="top-banner-text">Bouhdel s'engage : Maisons 100% RE2020 et garanties par le CCMI.</span>
                <div className="top-banner-links">
                  <Link to="/ccmi" className="top-banner-link">Qu'est-ce que le CCMI ?</Link>
                  <span className="top-banner-separator">•</span>
                  <Link to="/re2020" className="top-banner-link">Pourquoi la RE2020 ?</Link>
                </div>
                <span className="marquee-separator">✦</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile Dropdown */}
      <div id="mobile-menu" className={`nav-dropdown ${isMenuOpen ? 'nav-dropdown-open' : ''}`} inert={!isMenuOpen}>
        <div className="nav-dropdown-inner">
          <nav className="nav-dropdown-links">
            {sectionLinks.map((link, i) => (
              <Link
                key={link.id}
                to={`/#${link.id}`}
                onClick={(e) => handleSectionClick(e, link.id)}
                className="nav-dropdown-item"
                style={{ transitionDelay: isMenuOpen ? `${i * 45}ms` : '0ms' }}
              >
                {link.label}
              </Link>
            ))}

            {/* Pages links in mobile */}
            <div className="nav-dropdown-divider" />
            {expertiseLinks.map((link, i) => (
              <Link
                key={link.to}
                to={link.to}
                className="nav-dropdown-item nav-dropdown-page-link"
                onClick={() => setIsMenuOpen(false)}
                style={{ transitionDelay: isMenuOpen ? `${(sectionLinks.length + i) * 45}ms` : '0ms' }}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="nav-dropdown-cta">
            <button onClick={() => scrollToSection('contact')} className="btn-primary btn-dropdown-cta">
              Demander un devis
            </button>
          </div>

          <div className="nav-dropdown-footer">
            <a href={`tel:${mockData.company.phone}`} className="mobile-footer-link">{mockData.company.phone}</a>
            <a href={`mailto:${mockData.company.email}`} className="mobile-footer-link">{mockData.company.email}</a>
          </div>
        </div>
      </div>

      {/* Overlay */}
      {isMenuOpen && (
        <div
          className="mobile-overlay mobile-overlay-visible"
          onClick={() => setIsMenuOpen(false)}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default Header;
